import type { User } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { newSnippetId } from '@snippits/shared';
import { firebase } from './firebase';

export interface UploadResult {
  id: string;
  shareUrl: string;
  downloadUrl: string;
}

export function defaultTitle(date = new Date()): string {
  const formatted = date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
  return `Snippit · ${formatted}`;
}

interface CreateSnippetInput {
  id: string;
  title: string;
  width: number;
  height: number;
  storagePath: string;
  sizeBytes: number;
}

const createSnippet = httpsCallable<CreateSnippetInput, { id: string }>(
  firebase.functions,
  'createSnippet',
);

export async function uploadSnippet({
  user,
  dataUrl,
  width,
  height,
  title = defaultTitle(),
}: {
  user: User;
  dataUrl: string;
  width: number;
  height: number;
  title?: string;
}): Promise<UploadResult> {
  const id = newSnippetId();
  const storagePath = `snippets/${user.uid}/${id}.png`;

  const blob = await (await fetch(dataUrl)).blob();
  const storageRef = ref(firebase.storage, storagePath);
  await uploadBytes(storageRef, blob, {
    contentType: 'image/png',
    cacheControl: 'public, max-age=31536000, immutable',
  });

  await createSnippet({
    id,
    title,
    width,
    height,
    storagePath,
    sizeBytes: blob.size,
  });

  const downloadUrl = await getDownloadURL(storageRef);

  return {
    id,
    shareUrl: `${window.location.origin}/s/${id}`,
    downloadUrl,
  };
}
